import {useFieldArray, useFormContext} from "react-hook-form";
import {Link} from "react-router";

import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {Section} from "@/components/section/Section.tsx";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {StandardDateRangeLabels} from "@/lib/types/reports.ts";
import {DASHBOARD_CHART_TIME_RANGES, MAX_DASHBOARD_CHARTS} from "@/lib/types/website.ts";
import {DashboardFormFields} from "@/pages/dashboard/edit/dashboardForm.ts";
import {DEFAULT_CHART_TIME_RANGE} from "@/pages/dashboard/edit/subforms/dashboardFormCharts.types.ts";
import {useSavedViews} from "@/pages/metricsExplorer/savedViews/useSavedViews.ts";

import styles from "./DashboardFormCharts.module.scss";

const TIME_RANGE_OPTIONS = DASHBOARD_CHART_TIME_RANGES.map((range) => ({
  value: range,
  label: StandardDateRangeLabels[StandardDateRange[range]],
}));

/**
 * Places saved Metrics Explorer charts on the dashboard, each over its own window.
 *
 * Charts are referenced, not copied: editing one in the explorer updates every dashboard it is on.
 */
export const DashboardFormCharts = () => {
  const {control, watch} = useFormContext<DashboardFormFields>();
  const {fields, append, remove, move} = useFieldArray({control, name: "charts"});
  const {views} = useSavedViews(true);

  const showSection = watch("charts_show_section");
  const viewOptions = views.map((view) => ({value: view.id, label: view.name}));
  const canAdd = views.length > 0 && fields.length < MAX_DASHBOARD_CHARTS;

  return (
    <div className={styles.section}>
      <Section title="Charts" description="Saved Metrics Explorer charts, each over its own time range" />
      <Card className={styles.card}>
        <SwitchField labelPosition="left" name="charts_show_section" label="Show Charts section" />
        {showSection && (
          <>
            <Divider />
            {views.length === 0 ? (
              <p className={styles.empty}>
                No saved charts yet. Build one in the <Link to="/metrics-explorer">Metrics Explorer</Link> and save it
                to place it here.
              </p>
            ) : (
              <div className={styles.rows}>
                {fields.map((field, index) => (
                  <div key={field.id} className={styles.row}>
                    <div className={styles.view}>
                      <SelectField name={`charts.${index}.view_id`} label="Chart" options={viewOptions} />
                    </div>
                    <div className={styles.range}>
                      <SelectField
                        name={`charts.${index}.time_range`}
                        label="Time range"
                        options={TIME_RANGE_OPTIONS}
                      />
                    </div>
                    <div className={styles.actions}>
                      <Button variant="secondary" disabled={index === 0} onClick={() => move(index, index - 1)}>
                        Up
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={index === fields.length - 1}
                        onClick={() => move(index, index + 1)}
                      >
                        Down
                      </Button>
                      <Button variant="secondary" onClick={() => remove(index)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <div>
                  <Button
                    variant="secondary"
                    disabled={!canAdd}
                    onClick={() => append({view_id: views[0].id, time_range: DEFAULT_CHART_TIME_RANGE})}
                  >
                    Add chart
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};
